import React, { useRef } from 'react';
import { FavouriteButton, PrimaryLabel } from '@noths/polaris-client-ribbons-design-system';
import { getRect } from '@noths/polaris-client-utils';

import { Price } from 'src/components/atoms/Price';
import { ProductCardImage } from 'src/components/atoms/ProductCardImage';
import { DecoratedProductForDisplay, Product, ProductUrlType } from 'src/types';
import { getDeliveryByEventLabel } from 'src/utils';
import { FREE_UK_DELIVERY } from '../constants/copy';
import * as testIds from '../constants/testIds';
import * as styles from '../styles';

type ProductCode = Product['code'];

export interface ProductCardProps extends DecoratedProductForDisplay {
  headingLevel?: 'h2' | 'h3' | 'h4';
  updateFavourite: (productCode: ProductCode) => void;
  onProductClick: (
    productUrl: ProductUrlType,
    productCode: ProductCode,
    productOffset: number,
  ) => void;
  productIndex: number;
  disableImageLazyLoading?: boolean;
}

/*
  Destructure only click handlers in parameter, and spreading rest to "product" variable;
  this makes it easy to pass it as argument to "updateFavourite".
*/
export const ProductCard = ({ updateFavourite, onProductClick, ...product }: ProductCardProps) => {
  const {
    code,
    title,
    links,
    images,
    price,
    preSalePrices,
    onSale,
    salePercentage,
    freeDomesticDelivery,
    headingLevel: Heading = 'h3',
    isNew,
    partner,
    isSponsored,
    sponsoredProductData,
    isFavourited,
    productIndex,
    purchasable,
    disableImageLazyLoading,
    deliveryByEvent,
  } = product;
  const sectionRef = useRef<HTMLElement | null>(null);

  const sponsoredDataAttributes = isSponsored
    ? {
        ...sponsoredProductData,
        'data-elevaate-sno': productIndex,
        'data-elevaate-ppr': price.amount / 100,
        'data-elevaate-pcc': price.currency,
      }
    : null;

  return (
    <section
      css={styles.card}
      data-testid="product-card"
      className={isSponsored ? 'elevaate-product' : ''}
      ref={sectionRef}
      {...sponsoredDataAttributes}
    >
      <Heading style={{ margin: 0 }}>
        {/* eslint-disable-next-line */}
        <a
          onClick={(e) => {
            e.preventDefault();
            const offset = getRect(sectionRef?.current, 'top');
            onProductClick(links[0]?.rel as ProductUrlType, code, offset);
          }}
          href={links[0]?.rel || ''}
          css={styles.link}
          data-testid="PRODUCT_CARD__ANCHOR"
        >
          <div data-is-even={productIndex % 2 === 0} css={styles.imageWrapper}>
            {images[0] && (
              <ProductCardImage {...images[0]} disableLazyLoading={disableImageLazyLoading} />
            )}
          </div>
          {isSponsored && <div css={styles.sponsored}>Sponsored</div>}
          <div css={styles.copyWrapper}>
            <span css={styles.title}>{title}</span>
          </div>
        </a>
      </Heading>
      <div css={[styles.copyWrapper, styles.noMarginOnLastChild]}>
        <div css={styles.prices}>
          <Price
            testId={testIds.PRICE}
            isSalePrice={!!(onSale && preSalePrices[0])}
            css={styles.styledPrice}
            {...price}
          />
          {onSale && preSalePrices && preSalePrices[0] && (
            <Price
              testId={testIds.PREVIOUS_PRICE}
              isPreviousPrice={true}
              css={styles.previousPrice}
              {...preSalePrices[0]}
            />
          )}
        </div>
        <a href={partner.links[0]?.rel || ''} css={styles.partner}>
          {partner.name}
        </a>
        {deliveryByEvent?.arriveByEvent && (
          <p css={styles.labelWrapper}>
            <span css={styles.arrivesBy}>{getDeliveryByEventLabel(deliveryByEvent.eventName)}</span>
          </p>
        )}
        {freeDomesticDelivery && (
          <p css={styles.labelWrapper}>
            <span css={styles.freeDelivery}>{FREE_UK_DELIVERY}</span>
          </p>
        )}
      </div>
      <PrimaryLabel
        css={styles.productLabel}
        className="product-card__label"
        isNew={isNew}
        onSale={onSale}
        salePercentage={salePercentage}
        hasStock={purchasable}
      />
      <FavouriteButton
        css={[styles.favouriteButtonWrapper, styles.favouriteButtonClassicOverrides]}
        onClick={() => updateFavourite(product.code)}
        active={isFavourited}
        size="large"
      />
    </section>
  );
};
